import Swiper, { Pagination, Thumbs, Scrollbar } from 'swiper';
import 'swiper/css';
import getVariantFromSelection from '../utils/get-variant-from-selection.js';
import renderSectionFromResponse from '../utils/render-section-from-response.js';
import dispatchEvent from '../utils/dispatch-event.js';
import formatMoney from '../utils/currency.js';

const selectors = {
  sections: {
    productPageInfo: '.product-page-info',
  },
  carouselWrapper: '[data-product-gallery-image-carousel]',
  thumbnailWrapper: '[data-product-gallery-thumbnail-carousel]',
  gallerySlide: '.gallery-slide',
  galleryThumb: '.gallery-thumb',
};

class VariantSelector {
  constructor(element) {
    this.element = element;
    this.topSection = document.querySelector(
      selectors.sections.productPageInfo
    );

    this.initEventListeners();
    this.initThumbs();
    this.initGallery();
  }

  get currentVariant() {
    return JSON.parse(this.element.dataset.currentVariant);
  }

  get product() {
    return JSON.parse(this.element.dataset.product);
  }

  get variants() {
    return JSON.parse(this.element.dataset.allVariants);
  }

  get variantOptionGroups() {
    return this.element.querySelectorAll('[data-variant-option]');
  }

  switchVariant(option, value, optionIndex) {
    const variant = getVariantFromSelection(
      option,
      value,
      optionIndex,
      this.currentVariant,
      this.variants
    );

    // If we already have that variant selected, then do nothing.
    if (variant.id === this.currentVariant.id) {
      return;
    }

    this.loadInNewVariant(variant.id);

    document
      .getElementsByTagName('klarna-placement')[0]
      .setAttribute('data-purchase-amount', variant.price);
    window.KlarnaOnsiteService = window.KlarnaOnsiteService || [];
    window.KlarnaOnsiteService.push({ eventName: 'refresh-placements' });
  }

  initThumbs() {
    this.thumbnailCarousel = new Swiper(selectors.thumbnailWrapper, {
      slidesPerView: 6,
      slideToClickedSlide: true,
      spaceBetween: 10,
    });
  }

  initGallery() {
    this.galleryCarousel = new Swiper(selectors.carouselWrapper, {
      modules: [Pagination, Thumbs, Scrollbar],
      pagination: {
        el: '.swiper-pagination',
        type: 'bullets',
        clickable: true,
      },
      breakpoints: {
        1024: {
          pagination: false,
          scrollbar: {
            el: '.slider-scrollbar',
            draggable: true,
            hide: false,
            snapOnRelease: false,
            dragSize: 80,
          },
        },
      },
    });
  }

  switchSlides() {
    this.gallerySlide = document.querySelectorAll(selectors.gallerySlide);
    this.galleryThumb = document.querySelector(selectors.galleryThumb);

    const activeSlide = this.thumbnailCarousel.clickedIndex;

    setTimeout(() => {
      this.galleryCarousel.slideTo(
        this.gallerySlide[activeSlide].dataset.slide
      );
    }, 500);
  }

  loadInInitialVariant() {
    this.loadInNewVariant(this.currentVariant.id, true);
  }

  loadInNewVariant(id) {
    fetch(
      `/products/${this.product.handle}?sections=product-info&variant=${id}`
    )
      .then(response => response.json())
      .then(response => {
        renderSectionFromResponse(
          selectors.sections.productPageInfo,
          'product-info',
          response
        );

        this.topSection.classList.remove('opacity-50', 'pointer-events-none');

        this.updateVariantQueryParameter(id);

        [...document.querySelectorAll('[data-atc-form-container]')].forEach(
          container => {
            container.classList.remove('invisible', 'h-0', 'overflow-hidden');
          }
        );

        dispatchEvent('section:product-info:reload', document);
      });
  }

  /**
   * @param {String} sku
   * @returns {Object|null}
   */
  findVariantBySku(sku) {
    return this.variants.find(variant => variant.sku === sku);
  }

  // eslint-disable-next-line class-methods-use-this
  updateVariantQueryParameter(id) {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('variant', id);

    const newurl = `${window.location.protocol}//${window.location.host}${
      window.location.pathname
    }?${searchParams.toString()}`;

    window.history.pushState({ path: newurl }, '', newurl);
  }

  initEventListeners() {
    if (!this.element) {
      return;
    }

    this.element.addEventListener('variant-option:selected', e => {
      // this.initInfoCarousel();
      this.switchVariant(e.detail.option, e.detail.value, e.detail.index);
      this.switchSlides();
    });

    document.addEventListener('variant-sku:selected', e => {
      this.loadInNewVariant(this.findVariantBySku(e.detail.sku)?.id);
    });

    this.variantOptionGroups.forEach(group => {
      [...group.querySelectorAll('[data-option-selector]')].forEach(
        selector => {
          selector.addEventListener('click', e => {
            e.preventDefault();

            this.element.dispatchEvent(
              new CustomEvent('variant-option:selected', {
                detail: {
                  option: selector.dataset.option,
                  value: selector.dataset.value,
                  index: Number(selector.dataset.optionIndex),
                },
              })
            );
          });
        }
      );
    });

    const addToCartBtn = document.querySelectorAll('[data-atc-btn]');
    // eslint-disable-next-line
    const _learnq = window._learnq || [];

    const item = {
      Name: this.product.title,
      ProductID: this.product.id,
      ImageURL: this.product.featured_image,
      URL: window.location.href,
      Brand: this.product.vendor,
      Price: formatMoney(this.product.price),
      CompareAtPrice: formatMoney(this.product.compare_at_price_max),
    };

    addToCartBtn.forEach(btn => {
      // eslint-disable-next-line func-names
      btn.addEventListener('click', function () {
        _learnq.push(['track', 'Added to Cart', item]);
      });
    });
  }
}

export default VariantSelector;
